import React, {useEffect, useMemo, useState} from "react";
import Input from "../../../../../stories/Input/Input";
import {
  labelClasses,
  LabeledAutocomplete,
} from "../../../../../styles/AutocompleteStyles";
import {Keys} from "../../../../../utils/Enum.keys";
import {
  getModifiedScrollHeight,
  handleMUISelectEvent,
} from "../../../../../utils/UtilFunctions";
import {TextField} from "@mui/material";
import {StyledDatagrid} from "../../../../../styles/DatagridStyles";
import ItemGroupSelect from "../../../../../customComponents/ItemGroupSelect";
import ItemCategorySelect from "../../../../../customComponents/ItemCategorySelect";
import {
  AtsStckItemDetailsEdges,
  AtsStckItemDetailsOrderField,
  filterDataType,
  GetItemsDetailsResult,
  GetItemsDetailsVars,
  ItemDetailsQueryType,
} from "../../masters/items/queries/types";
import {GetItemsDetails} from "../../masters/items/queries/queries";
import {useLazyQuery} from "@apollo/client";
import useUrlParams from "../../../../../customHooks/useUrlParams";

import {FETCH_MORE_DATA, ROWS_PER_PAGE} from "../../../../../utils/constants";
import {OrderDirection} from "../../../../../utils/commonQueryTypes";
import {TableHeaderProps} from "../../../../../utils/types";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import {TABLE_ROW_HEIGHT} from "../../../../../styles/Variables";
import {ColumnVisibilityFor} from "../../../../../utils/Enum.Datagrid";
import NoData from "../../../../../images/NoData.svg";
import View from "../../../../../images/EyeWhite.svg";
import {Button} from "../../../../../stories/Button/Button";
import ViewAssetDetails from "./ViewAssetDetails";
import useLabels from "../../../../../customHooks/useLabels";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";

interface Props {
  sub_id: number;
}
const AssetTableView = ({sub_id}: Props) => {
  const {customer_id, company_id} = useUrlParams();

  const {user_details} = useLoggedInUserDetails();

  const [searchData, setSearchData] = useState("");
  const {Items} = useAssetTableJson();
  const {itemLabel} = useLabels();
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [viewDetails, setViewDetails] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [filterData, setFilterData] = useState<filterDataType>({
    ItemGroupId: 0,
    ItemCategoryId: 0,
    UoMId: 0,
  });

  const [GetSubsidiaryItems, {data, loading, fetchMore}] = useLazyQuery<
    GetItemsDetailsResult,
    GetItemsDetailsVars
  >(GetItemsDetails);
  useEffect(() => {
    if (customer_id || company_id) {
      GetSubsidiaryItems({
        variables: {
          customerID: customer_id ? customer_id : 0,
          companyID: company_id ? company_id : 0,
          subsidiaryID: sub_id ? sub_id : 0,
          junctionID: 0,
          siteID: 0,
          input: {
            query_type: ItemDetailsQueryType.ALL,
            stckCategoryID: filterData.ItemCategoryId
              ? filterData.ItemCategoryId
              : 0,
            stckGroupID: filterData.ItemGroupId ? filterData.ItemGroupId : 0,
            stckManufacturerID: 0,
            stckUomID: filterData.UoMId ? filterData.UoMId : 0,
            stckSubCategoryID: 0,
            itemID: 0,
            ldgrID: 0,
            poleNum: 0,
            empID: user_details.id,
          },
          after: null,
          first: ROWS_PER_PAGE,
          name: searchData,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsStckItemDetailsOrderField.CREATED_AT,
          },
        },
      });
    }
  }, [customer_id, sub_id, GetSubsidiaryItems, searchData, filterData]);

  const dynamicHeaders: TableHeaderProps[] = Items.List.filter(
    (header) => header.field !== "total_asset"
  ).map((header) => ({
    headerName: header.headerName,
    field: header.field,
    headerAlign: header.headerAlign as GridAlignment,
    align: header.align as GridAlignment,
    flex: header.flex,
    hideable: header.hideable,
    cellClassName: header.cellClassName,
    renderCell: (params) => {
      if (header.field === "actions") {
        return (
          <>
            <button
              className="tkt-raised-list__tableblock--view"
              onClick={() => setViewDetails(!viewDetails)}
            >
              View Details <img src={View} alt="/" />
            </button>
          </>
        );
      } else return params.value;
    },
  }));
  const columns: GridColDef[] = [...dynamicHeaders];
  const [items, setItems] = useState<AtsStckItemDetailsEdges[]>([]);

  const rows1: GridValidRowModel[] = useMemo(() => {
    return items.map(({node: data}, index) => {
      return {
        id: index + 1,
        code: data.item.code,
        description: data.item.desc,
        group: data.item.groupDetails.groupName,
        category: data.item.categoryDetails.categoryName,
        sub_category: data.item.subCategoryDetails.subCategoryName,
        model: data.prodModel,
        uom: data.item.uomDetails.uomDesc,
      };
    });
  }, [items]);
  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemsDetails.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
      } else {
        setItems(newData);
      }
      setEndCursor(data.GetItemsDetails.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);
  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, {fetchMoreResult}) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetItemsDetails.edges;
              const pageInfo = fetchMoreResult.GetItemsDetails.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetItemsDetails.edges.filter(
                ({node: {id}}) =>
                  newEdges.findIndex(({node: {id: newId}}) => newId === id) !==
                  -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetItemsDetails: {
                  edges: [...items, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetItemsDetails.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable) scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [items, data]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(ColumnVisibilityFor.ITEMS_LIST);
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.ITEMS_LIST,
      JSON.stringify(newModel)
    );
  };
  return (
    <>
      {!viewDetails ? (
        <>
          <div className="tkt-raised-list__select row g-0">
            <div className="col">
              <Input id="data_search" placeholder="Search..." />
            </div>
            <div className="col">
              <ItemGroupSelect
                onChange={(newValue) => {
                  if (newValue) {
                    setFilterData({
                      ...filterData,
                      ItemGroupId: newValue.value,
                    });
                  } else {
                    setFilterData({
                      ...filterData,
                      ItemGroupId: 0,
                    });
                  }
                }}
                value={filterData.ItemGroupId}
                onKeyDown={handleMUISelectEvent}
                label={`${itemLabel} Group`}
              />
            </div>
            <div className="col">
              <ItemCategorySelect
                onChange={(newValue) => {
                  if (newValue) {
                    setFilterData({
                      ...filterData,
                      ItemCategoryId: newValue.value,
                    });
                  } else {
                    setFilterData({
                      ...filterData,
                      ItemCategoryId: 0,
                    });
                  }
                }}
                value={filterData.ItemCategoryId}
                label={`${itemLabel} Category`}
                onKeyDown={handleMUISelectEvent}
              />
            </div>
            <div className="col">
              <LabeledAutocomplete
                className={labelClasses.inputRoot}
                onKeyDown={(e) => {
                  if (e.key === Keys.ENTER) {
                    e.preventDefault();
                    handleMUISelectEvent(e);
                  }
                }}
                options={["a", "b"]}
                openOnFocus
                forcePopupIcon
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    className={labelClasses.formControlRoot}
                    label="Sub Category"
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                )}
              />
            </div>
            <div className="col">
              <LabeledAutocomplete
                className={labelClasses.inputRoot}
                onKeyDown={(e) => {
                  if (e.key === Keys.ENTER) {
                    e.preventDefault();
                    handleMUISelectEvent(e);
                  }
                }}
                options={["a", "b"]}
                openOnFocus
                forcePopupIcon
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    className={labelClasses.formControlRoot}
                    label="Model"
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                )}
              />
            </div>
            <div className="col">
              <LabeledAutocomplete
                className={labelClasses.inputRoot}
                onKeyDown={(e) => {
                  if (e.key === Keys.ENTER) {
                    e.preventDefault();
                    handleMUISelectEvent(e);
                  }
                }}
                options={["a", "b"]}
                openOnFocus
                forcePopupIcon
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    className={labelClasses.formControlRoot}
                    label="Manufacturer"
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                )}
              />
            </div>
            <div className="col">
              <LabeledAutocomplete
                className={labelClasses.inputRoot}
                onKeyDown={(e) => {
                  if (e.key === Keys.ENTER) {
                    e.preventDefault();
                    handleMUISelectEvent(e);
                  }
                }}
                options={["a", "b"]}
                openOnFocus
                forcePopupIcon
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    className={labelClasses.formControlRoot}
                    label="HSN"
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className="tkt-raised-list__tableblock">
            {items.length ? (
              <StyledDatagrid
                columns={columns}
                rows={rows1}
                rowHeight={TABLE_ROW_HEIGHT}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                initialState={{
                  columns: {
                    columnVisibilityModel: columnVisibilityModel,
                  },
                }}
                disableRowSelectionOnClick
                hideFooter
              />
            ) : (
              <>
                <img src={NoData} alt="" className="nodata-image" />
              </>
            )}
          </div>
          <div className="row g-0">
            <div className="col">
              <Button mode="excel">Export</Button>
              <Button mode="back" />
            </div>
            <div className="col flex-end">
              <div className="overall-total-count">
                Total {itemLabel} : <b>{items.length}</b>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <ViewAssetDetails />
        </>
      )}
    </>
  );
};

export default AssetTableView;
